import { z } from 'zod';
import { JsonValue } from '@backstage/types';
import { ok, err, Result } from '../error';

export const booleanQueryParamSchema = z
  .enum(['true', 'false'])
  .transform(v => v === 'true');

export const numberQueryParamSchema = z.preprocess(
  val => (val === '' ? undefined : val),
  z.coerce.number(),
);

export const integerQueryParamSchema = z.preprocess(
  val => (val === '' ? undefined : val),
  z.coerce.number().int(),
);

export const validateHttpUrl = (val: string, ctx: z.RefinementCtx) => {
  let url: URL;
  try {
    url = new URL(val);
  } catch {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid URL' });
    return;
  }

  if (!['http:', 'https:'].includes(url.protocol)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Must use http or https',
    });
  }
};

/**
 * Validates that a value matches the schema at a specific path
 *
 * @param path - Path to the schema property
 * @param value - The value to validate
 * @param schema - The root Zod schema
 * @returns Whether the value is valid according to the schema at the path
 */
export function validateAtPath(
  path: string[],
  value: JsonValue,
  schema: z.ZodTypeAny,
): Result<null, z.ZodError> {
  const resolvedSchema = resolveSchemaAtPath(schema, path);
  if (resolvedSchema.isError) return err(resolvedSchema.error);
  const result = resolvedSchema.value.safeParse(value);
  return result.success ? ok(null) : err(result.error);
}

function unwrapZodSchema(schema: z.ZodTypeAny): z.ZodTypeAny {
  let current = schema;
  while (
    current instanceof z.ZodOptional ||
    current instanceof z.ZodNullable ||
    current instanceof z.ZodDefault ||
    current instanceof z.ZodCatch ||
    current instanceof z.ZodEffects
  ) {
    current =
      current instanceof z.ZodEffects
        ? current._def.schema
        : current._def.innerType;
  }
  return current;
}

function schemaPathError(path: string[], message: string): z.ZodError {
  return new z.ZodError([{ code: 'custom', path, message }]);
}

/**
 * Resolves the Zod schema at a specific path within a nested schema structure
 *
 * @param schema - The root Zod schema
 * @param path - Path to resolve (array of keys/indices)
 * @returns The resolved schema at the path, or an error if path is invalid
 */
export function resolveSchemaAtPath(
  schema: z.ZodTypeAny,
  path: string[],
): Result<z.ZodTypeAny, z.ZodError> {
  if (!path.length) return ok(schema);

  const parts = path;
  let subschema = schema;

  for (const [index, part] of parts.entries()) {
    subschema = unwrapZodSchema(subschema);

    if (subschema instanceof z.ZodAny || subschema instanceof z.ZodUnknown) {
      return ok(subschema);
    }

    if (subschema instanceof z.ZodArray) {
      const arrayIndex = Number(part);
      if (Number.isNaN(arrayIndex)) {
        return err(
          schemaPathError(
            parts.slice(0, index + 1),
            `Expected array index at '${part}'`,
          ),
        );
      }
      subschema = subschema._def.type;
    } else if (subschema instanceof z.ZodTuple) {
      const tupleIndex = Number(part);
      if (
        Number.isNaN(tupleIndex) ||
        tupleIndex < 0 ||
        tupleIndex >= subschema._def.items.length
      ) {
        return err(
          schemaPathError(
            parts.slice(0, index + 1),
            `Invalid tuple index '${part}'`,
          ),
        );
      }
      subschema = subschema._def.items[tupleIndex];
    } else if (subschema instanceof z.ZodObject) {
      const shape = subschema.shape;
      if (!(part in shape)) {
        const catchall = subschema._def.catchall;
        if (catchall && !(catchall instanceof z.ZodNever)) {
          subschema = catchall;
        } else {
          return err(
            schemaPathError(
              parts.slice(0, index + 1),
              `Path '${part}' does not exist in schema`,
            ),
          );
        }
      } else {
        subschema = shape[part];
      }
    } else if (subschema instanceof z.ZodRecord) {
      subschema = subschema._def.valueType;
    } else {
      return err(
        schemaPathError(
          parts.slice(0, index + 1),
          `Expected object or array at '${part}'`,
        ),
      );
    }
  }

  return ok(subschema);
}
